import {
	Component,
	forwardRef,
	Injector,
	Input,
	OnChanges,
	OnInit,
	OnDestroy,
	SimpleChanges,
} from '@angular/core';
import {
	ControlValueAccessor,
	FormControl,
	NG_VALUE_ACCESSOR,
	NgControl,
	ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Subject, Subscription, takeUntil } from 'rxjs';


interface TimeOption {
	value: string;
	display: string;
	hour: number;
}

@Component({
	selector: 'time-range-picker',
	templateUrl: './time-range-picker.component.html',
	styleUrls: ['./time-range-picker.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatSelectModule,
		MatSlideToggleModule,
	],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => TimeRangePickerComponent),
			multi: true,
		},
	],
})
export class TimeRangePickerComponent
	implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
	@Input() limitStartTime: number = 9; // Default 9:00 AM
	@Input() limitEndTime: number = 18; // Default 6:00 PM (18:00)
	@Input() title: string = 'Time Range'; // Default title

	private readonly _onDestroy = new Subject<void>();
	private statusChangesSubscription: Subscription | undefined;
	private startTimeControlValueChangesSubscription: Subscription | undefined;
	private endTimeControlValueChangesSubscription: Subscription | undefined;

	protected startTimeControl = new FormControl('');
	protected endTimeControl = new FormControl('');

	protected startTimeOptions: TimeOption[] = [];
	protected endTimeOptions: TimeOption[] = [];

	protected isRequired: boolean = false;

	constructor(private readonly injector: Injector) { }

	public ngOnInit(): void {
		this.generateStartTimeOptions();
		this.setupFormSubscriptions();
		this.detectRequiredValidator();
		const ngControl = this.injector.get(NgControl, null);
		this.statusChangesSubscription = ngControl?.statusChanges?.pipe(takeUntil(this._onDestroy)) // Prevents memory leaks
			.subscribe(() => {
				// Add custom logic, e.g., enabling other fields
				this.detectRequiredValidator();
			});
	}

	public ngOnChanges(changes: SimpleChanges): void {
		// Regenerate time options if limit times change
		if (changes['limitStartTime'] || changes['limitEndTime']) {
			this.generateStartTimeOptions();
			this.validateAndResetFormValues();
		}
	}

	public ngOnDestroy() {
		this.statusChangesSubscription?.unsubscribe();
		this.startTimeControlValueChangesSubscription?.unsubscribe();
		this.endTimeControlValueChangesSubscription?.unsubscribe();
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	// ControlValueAccessor implementation
	public writeValue(value: string | null): void {
		const parsedValue = this.parseTimeRangeString(value);
		if (parsedValue) {
			// Ensure options are generated first
			if (this.startTimeOptions.length === 0) {
				this.generateStartTimeOptions();
			}

			// Set start time first
			this.startTimeControl.setValue(parsedValue.startTime || '', {
				emitEvent: false,
			});

			// Use setTimeout to ensure the control value is properly set before updating end time options
			setTimeout(() => {
				this.updateEndTimeOptions();
				this.updateEndTimeDisabledState();
				this.endTimeControl.setValue(parsedValue.endTime || '', {
					emitEvent: false,
				});
			}, 0);
		} else {
			this.startTimeControl.setValue('', { emitEvent: false });
			this.endTimeControl.setValue('', { emitEvent: false });
			this.endTimeOptions = [];
			this.updateEndTimeDisabledState();
		}
	}

	public registerOnChange(fn: (value: string | null) => void): void {
		this.onChange = fn;
	}

	public registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	public setDisabledState(isDisabled: boolean): void {
		if (isDisabled) {
			this.startTimeControl.disable({ emitEvent: false });
			this.endTimeControl.disable({ emitEvent: false });
		} else {
			this.startTimeControl.enable({ emitEvent: false });
			this.updateEndTimeDisabledState();
		}
	}

	// Validation helper methods for template
	protected isInvalid(): boolean {
		const startTime = this.startTimeControl.value;
		const endTime = this.endTimeControl.value;
		const touched =
			this.startTimeControl.touched || this.endTimeControl.touched;

		// Invalid scenarios:
		// 1. If required: both empty and touched
		// 2. Always invalid: start time selected but no end time (incomplete selection)
		return (
			touched &&
			((this.isRequired && !startTime && !endTime) || // Required but both empty
				(startTime && !endTime)) // Incomplete - always invalid
		);
	}

	protected shouldShowStartTimeError(): boolean {
		// Only show start time error when it's required and empty
		return (
			this.isRequired &&
			!this.startTimeControl.value &&
			this.startTimeControl.touched
		);
	}

	protected shouldShowEndTimeError(): boolean {
		const startTime = this.startTimeControl.value;
		const endTime = this.endTimeControl.value;
		const touched =
			this.startTimeControl.touched || this.endTimeControl.touched;

		// Show end time error when:
		// 1. Required and no end time and touched, OR
		// 2. Start time is selected but no end time (always show - regardless of required)
		return (
			(this.isRequired && !endTime && this.endTimeControl.touched) ||
			(startTime && !endTime && touched)
		);
	}

	private onChange = (value: string | null) => { };

	private onTouched = () => { };

	private generateStartTimeOptions(): void {
		this.startTimeOptions = [];

		// Start time options should exclude the last hour (limitEndTime)
		// because end time must be after start time
		for (let hour = this.limitStartTime; hour < this.limitEndTime; hour++) {
			const time12Hour = this.convertTo12Hour(hour);
			this.startTimeOptions.push({
				value: `${hour.toString().padStart(2, '0')}:00`,
				display: time12Hour,
				hour: hour,
			});
		}
	}

	private convertTo12Hour(hour: number): string {
		if (hour === 0) return '12:00 AM';
		if (hour === 12) return '12:00 PM';
		if (hour < 12) return `${hour}:00 AM`;
		return `${hour - 12}:00 PM`;
	}

	private setupFormSubscriptions(): void {
		let previousStartTime = this.startTimeControl.value;

		// Update end time options when start time changes
		this.startTimeControlValueChangesSubscription = this.startTimeControl.valueChanges.subscribe((newStartTime) => {
			// Only clear end time if start time actually changed, not on enable/disable
			if (newStartTime !== previousStartTime) {
				this.endTimeControl.setValue('');
			}

			previousStartTime = newStartTime;
			this.updateEndTimeOptions();
			this.updateEndTimeDisabledState();
			this.emitValue();
			this.onTouched();
		});

		// Emit value when end time changes
		this.endTimeControlValueChangesSubscription = this.endTimeControl.valueChanges.subscribe(() => {
			this.emitValue();
			this.onTouched();
		});

		// Initial state setup
		this.updateEndTimeDisabledState();
	}

	private updateEndTimeOptions(): void {
		const startTimeValue = this.startTimeControl.value;

		if (!startTimeValue) {
			this.endTimeOptions = [];
			return;
		}

		// Extract hour from start time (format: "HH:MM")
		const startHour = parseInt(startTimeValue.split(':')[0]);

		// Generate end time options from start+1 to limitEndTime (inclusive)
		this.endTimeOptions = [];
		for (let hour = startHour + 1; hour <= this.limitEndTime; hour++) {
			const time12Hour = this.convertTo12Hour(hour);
			this.endTimeOptions.push({
				value: `${hour.toString().padStart(2, '0')}:00`,
				display: time12Hour,
				hour: hour,
			});
		}
	}

	private updateEndTimeDisabledState(): void {
		const shouldDisable =
			!this.startTimeControl.value || this.startTimeControl.disabled;

		if (shouldDisable) {
			this.endTimeControl.disable({ emitEvent: false });
		} else {
			this.endTimeControl.enable({ emitEvent: false });
		}
	}

	private parseTimeRangeString(
		timeRangeString: string | null
	): { startTime: string; endTime: string } | null {
		if (!timeRangeString || typeof timeRangeString !== 'string') {
			return null;
		}

		// Handle format like "09:00-10:00"
		const match = /^(\d{2}:\d{2})-(\d{2}:\d{2})$/.exec(timeRangeString);
		if (match) {
			return {
				startTime: match[1],
				endTime: match[2],
			};
		}

		return null;
	}

	private emitValue(): void {
		const startTime = this.startTimeControl.value;
		const endTime = this.endTimeControl.value;

		// Only emit valid string when BOTH start and end time are selected
		// This ensures form is invalid when incomplete
		if (startTime && endTime) {
			this.onChange(`${startTime}-${endTime}`);
		} else {
			// Emit null when incomplete - this makes the form control invalid
			this.onChange(null);
		}
	}

	private validateAndResetFormValues(): void {
		const startValue = this.startTimeControl.value;
		const endValue = this.endTimeControl.value;

		if (startValue) {
			const hour = parseInt(startValue.split(':')[0]);
			if (hour < this.limitStartTime || hour > this.limitEndTime) {
				this.startTimeControl.setValue('');
			}
		}

		if (endValue) {
			const hour = parseInt(endValue.split(':')[0]);
			if (hour < this.limitStartTime || hour > this.limitEndTime) {
				this.endTimeControl.setValue('');
			}
		}

		this.updateEndTimeOptions();
	}

	private detectRequiredValidator(): void {
		// Get the NgControl (FormControl) from parent
		const ngControl = this.injector.get(NgControl, null);
		if (ngControl?.control) {
			// Check if the control has required validator
			const validators = ngControl.control.validator;
			if (validators) {
				// Test with null value to see if required error occurs
				const errors = validators({ value: null } as any);
				this.isRequired = errors?.required ?? false;
			}
		}
	}
}
